import React, { createContext, useContext, useState, useEffect } from 'react';
import { useToast } from './ToastContext';
import { useTranslation } from 'react-i18next';

const AuthModalContext = createContext();

export const useAuthModal = () => {
  const context = useContext(AuthModalContext);
  if (!context) {
    throw new Error('useAuthModal must be used within AuthModalProvider');
  }
  return context;
};

export const AuthModalProvider = ({ children }) => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  const [authPrompt, setAuthPrompt] = useState(null);
  const { addToast } = useToast();
  const { t } = useTranslation();

  const openLoginModal = (promptData = null) => {
    if (promptData && typeof promptData === 'object' && !promptData.nativeEvent) {
      setAuthPrompt(promptData);
    }
    setShowRegisterModal(false);
    setShowLoginModal(true);
  };

  const openRegisterModal = (promptData = null) => {
    if (promptData && typeof promptData === 'object' && !promptData.nativeEvent) {
      setAuthPrompt(promptData);
    }
    setShowLoginModal(false);
    setShowRegisterModal(true);
  };

  const closeModals = () => {
    setShowLoginModal(false);
    setShowRegisterModal(false);
    setAuthPrompt(null);
  };

  // Глобальний слухач для подій авторизації
  useEffect(() => {
    const handleAuthRequired = (event) => {
      const detail = event.detail || {};
      const { messageKey, message: rawMessage, pendingSaveRecipeId, recipeTitle, mode, reason } = detail;

      if (pendingSaveRecipeId) {
        try {
          localStorage.setItem('pendingSaveRecipeId', String(pendingSaveRecipeId));
          if (recipeTitle) {
            localStorage.setItem('pendingSaveRecipeTitle', String(recipeTitle));
          }
        } catch (_) {}
      }

      if (recipeTitle || pendingSaveRecipeId || reason) {
        setAuthPrompt({
          recipeTitle: recipeTitle || '',
          pendingSaveRecipeId: pendingSaveRecipeId || null,
          reason: reason || 'save'
        });
      } else {
        setAuthPrompt(null);
        const message = messageKey
          ? t(messageKey)
          : (rawMessage || t('auth.requiredDefault'));
        addToast(message, 'warning');
      }

      if (mode === 'register' || pendingSaveRecipeId) {
        setShowLoginModal(false);
        setShowRegisterModal(true);
      } else {
        setShowRegisterModal(false);
        setShowLoginModal(true);
      }
    };

    window.addEventListener('authRequired', handleAuthRequired);

    return () => {
      window.removeEventListener('authRequired', handleAuthRequired);
    };
  }, [addToast, t]);

  const value = {
    showLoginModal,
    showRegisterModal,
    authPrompt,
    openLoginModal,
    openRegisterModal,
    closeModals
  };

  return (
    <AuthModalContext.Provider value={value}>
      {children}
    </AuthModalContext.Provider>
  );
};
